import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ACTIVE_ORG_STORAGE_KEY } from "@/lib/constants";

import { getSession, switchOrg as switchOrgApi } from "./api/organization-api";
import { SessionContext, type SessionData } from "./session-context";
import type { SessionMembershipRole } from "./types";

interface SessionProviderProps {
  children: ReactNode;
}

function deriveAvailableRoles(memberships: SessionData["memberships"]): SessionMembershipRole[] {
  const uniqueRoles = new Map<string, SessionMembershipRole>();
  for (const m of memberships) {
    for (const r of m.roles) {
      uniqueRoles.set(r.role_id, r);
    }
  }
  return [...uniqueRoles.values()];
}

export function SessionProvider({ children }: SessionProviderProps): ReactElement {
  const [session, setSession] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const requestIdRef = useRef(0);

  const fetchSession = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    try {
      const response = await getSession();
      if (requestId !== requestIdRef.current) return;
      const { active_org_unit, memberships } = response.data;

      // A session that does not carry its permissions grants none: the
      // console offers a control only for a permission it was told about.
      const permissions: readonly string[] = Array.isArray(response.data.permissions)
        ? response.data.permissions
        : [];

      const sessionData: SessionData = {
        activeOrgUnit: active_org_unit,
        memberships,
        availableRoles: deriveAvailableRoles(memberships),
        permissions,
      };

      setSession(sessionData);

      if (active_org_unit) {
        localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, active_org_unit.id);
      } else {
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      setSession(null);
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    void fetchSession().finally(() => setIsLoading(false));
  }, [fetchSession]);

  const switchOrg = useCallback(
    async (orgUnitId: string): Promise<void> => {
      setIsSwitching(true);
      try {
        await switchOrgApi(orgUnitId);
        await fetchSession();
      } finally {
        setIsSwitching(false);
      }
    },
    [fetchSession]
  );

  const refreshSession = useCallback(async (): Promise<void> => {
    await fetchSession();
  }, [fetchSession]);

  const value = useMemo(
    () => ({
      session,
      isLoading,
      isSwitching,
      switchOrg,
      refreshSession,
    }),
    [session, isLoading, isSwitching, switchOrg, refreshSession]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
